import { Component, TemplateRef, ViewChild } from "@angular/core";
import { Breadcrumb } from "@shared/components";
import { NgGenerateTableColumns } from "ng-generate-table";
import { NgBsToastService } from "ng-bs-toast-service";
import { ActiveConsultationStateService, Consultation, ConsultationFilters, ConsultationsService, CurrentConsultationResponse, GRAVIDADE_1_A_5 } from "./";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpErrorResponse } from "@angular/common/http";
import { FrontPagination } from "@shared/components";
import { TranslatePipe } from "@shared/pipes";
import { Doctor, DoctorsService } from "../doctors";
import dayjs from "dayjs";
import { catchError, concat, distinctUntilChanged, map, Observable, of, Subject, switchMap, tap } from "rxjs";
import { NgBsModalService } from "ng-bs-modal-service";

@Component({
    selector: "app-consultations",
    standalone: false,
    templateUrl: "./consultations.component.html",
    providers: [TranslatePipe],
})
export class ConsultationsComponent {
    loading = { main: true, modal: false, doctors: false, modalUpload: false };
    doctors: Doctor[] = [];
    consultations: Consultation[];
    currentConsultation: Consultation;
    activeConsultation$: Observable<CurrentConsultationResponse | null>;
    selectedVideoFile: File | null = null;

    doctor$: Observable<Doctor[]>;
    doctorInput$ = new Subject<string>();

    pagination: FrontPagination = { page: 1, total: 0, pageSize: 10 };
    filters: ConsultationFilters = {
        startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
        endDate: dayjs().format("YYYY-MM-DD"),
    };

    breadcrumb: Breadcrumb[] = [
        // { title: 'Inicio', route: ['/'] },
        { title: "Consultas" },
    ];

    columns: NgGenerateTableColumns[] = [
        { label: "Título", template: (rowData: Consultation) => (rowData.title && rowData.title !== "null" ? rowData.title?.substring(0, 60) : "Sem título") },
        { label: "Data", field: "date" },
        { label: "Inicio", field: "createdAt" },
        { label: "Fim", field: "finishedAt" },

        // {
        //     label: "Status",
        //     template: (rowData: Consultation) => this.translatePipe.transform(rowData.statusEncerramento!),
        // },
        // {
        //     label: "Triagem",
        //     template: (rowData: Consultation) => this.getScore(rowData.gravidade1a5!),
        // },
        {
            label: "CID",
            template: (rowData: Consultation) => {
                if (!rowData.cidProvavel || rowData.cidProvavel === "- - -") return "---";
                const desc = rowData.cidDescricao && rowData.cidDescricao !== "- - -" ? rowData.cidDescricao : "";
                return desc ? `<span data-bs-toggle="tooltip" title="${this.escapeHtml(desc)}">${rowData.cidProvavel}</span>` : rowData.cidProvavel;
            },
        },
        {
            label: "Médico",
            template: (rowData: Consultation) => rowData.nameDoctor || "---",
        },
        {
            tdClass: "td-w-auto",
            template: () =>
                `<button type="button" class="btn btn-sm btn-link link-primary p-0">
                    <i class="bi bi-file-text me-1"></i>Detalhes
                 </button>`,
            click: (rowData: Consultation) => this.router.navigate(["consultations/view", rowData.id], {
                queryParams: { ...this.filters, page: this.pagination.page },
            }),
        },
    ];

    constructor(
        private doctorsService: DoctorsService,
        private consultationsService: ConsultationsService,
        private toastService: NgBsToastService,
        private router: Router,
        private route: ActivatedRoute,
        private modalService: NgBsModalService,
        private activeConsultationState: ActiveConsultationStateService,
    ) {
        this.activeConsultation$ = this.activeConsultationState.activeConsultation$;
    }

    @ViewChild("uploadFooter", { static: true }) uploadFooter: TemplateRef<any>;
    @ViewChild("uploadBody", { static: true }) uploadBody: TemplateRef<any>;
    openModal(footer: TemplateRef<any>) {
        this.modalService.open({
            header: "Upload Vídeo",
            body: this.uploadBody,
            footer,
        });
    }

    closeModal() {
        this.modalService.close();
    }

    onVideoSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedVideoFile = input.files[0];
        }
    }

    submitUploadVideo(): void {
        if (!this.selectedVideoFile) {
            console.warn("Nenhum vídeo selecionado.");
            return;
        }

        this.loading.modalUpload = true;

        this.consultationsService.uploadVideo(this.selectedVideoFile).subscribe({
            next: (res) => {
                this.toastService.send("Sucesso", "Vídeo enviado com sucesso!", "success");
                this.closeModal();
                this.getAll();
                this.loading.modalUpload = false;
            },
            error: (err: HttpErrorResponse) => {
                this.toastService.send(`Error ${err.status}`, err.error || err.name, "danger");
                this.loading.modalUpload = false;
            },
        });
    }

    ngOnInit() {
        const qp = this.route.snapshot.queryParams;
        if (qp["startDate"]) this.filters.startDate = qp["startDate"];
        if (qp["endDate"]) this.filters.endDate = qp["endDate"];
        if (qp["doctorId"]) this.filters.doctorId = qp["doctorId"];
        if (qp["cid"]) this.filters.cid = qp["cid"];
        if (qp["cidDescricao"]) this.filters.cidDescricao = qp["cidDescricao"];
        const restoredPage = qp["page"] ? Number(qp["page"]) : 1;

        this.getDoctors(this.filters.doctorId);
        this.getAll(restoredPage);
    }

    getAll(page = 1) {
        this.loading.main = true;
        this.pagination.page = page;
        this.router.navigate([], {
            queryParams: { ...this.filters, page },
            replaceUrl: true,
        });
        this.consultationsService
            .getAll(this.pagination, this.filters)
            .subscribe({
                next: (res) => {
                    this.pagination.total = res.total;
                    this.consultations = res.data.map((data: Consultation) => {
                        data.date = dayjs(data.createdAt).format("DD/MM/YYYY");
                        data.createdAt = dayjs(data.createdAt).format("HH:mm");
                        data.finishedAt = data.finishedAt ? dayjs(data.finishedAt).format("HH:mm") : undefined;
                        return data;
                    });
                },
                error: (err: HttpErrorResponse) => this.toastService.send(`Error ${err.status}`, err.error || err.name, "danger"),
            })
            .add(() => (this.loading.main = false));
    }

    escapeHtml(value: string) {
        return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    }

    getScore(score: string) {
        const idx = Number(score);
        const config = idx ? GRAVIDADE_1_A_5[idx] : { title: "SEM TRIAGEM", color: "text-bg-light" };
        return `<span class="badge ${config.color}">${config.title}</span>`;
    }

    openActiveConsultation(consultation: CurrentConsultationResponse) {
        if (!consultation.id) return;
        this.router.navigate(["consultations/active", consultation.id]);
    }

    getActivePatientName(consultation: CurrentConsultationResponse) {
        return consultation.patient?.name || consultation.patient?.nome || consultation.paciente?.name || consultation.paciente?.nome || consultation.patientName || "---";
    }

    getActivePatientSex(consultation: CurrentConsultationResponse) {
        return consultation.patient?.sex || consultation.patient?.sexo || consultation.paciente?.sex || consultation.paciente?.sexo || consultation.patientSex || consultation.sex || consultation.sexo || "---";
    }

    private getDoctors(initialDoctorId?: string) {
        const page = 1;
        const pageSize = 1000;

        const initial$: Observable<Doctor[]> = initialDoctorId
            ? this.doctorsService.getById(initialDoctorId).pipe(
                  map((doc) => [doc]),
                  catchError(() => of([])),
              )
            : of([]);

        this.doctor$ = concat(
            initial$,
            this.doctorInput$.pipe(
                distinctUntilChanged(),
                tap(() => (this.loading.doctors = true)),
                switchMap((term) => {
                    return term && term.length > 2
                        ? this.doctorsService.getByTerm(term, page, pageSize).pipe(
                              map((res) => res.data),
                              catchError(() => of([])), // empty list on error
                              tap(() => (this.loading.doctors = false)),
                          )
                        : new Promise<Doctor[]>(() => (this.loading.doctors = false)); // not search all
                }),
            ),
        );
    }
}
